// Canonical afianzadora naming.
//
// The XML emisor carries the full legal name ("CHUBB FIANZAS MONTERREY,
// ASEGURADORA DE CAUCIÓN, S.A.") which is useless as a grouping key: every
// report that groups by afianzadora needs the short trade name.
//
// THE CATALOG IS THE SOURCE OF TRUTH. `afianzadoras/{slug}.nombre` (global
// collection, admin-written, seeded by scripts/seed_afianzadoras.py) holds the
// display name. Matching works by looking for a catalog name inside the emisor
// string — so the "pattern" IS the trade name, and there is no separate list of
// invented rules to drift out of sync. Renaming a carrier is a Console edit,
// not a deploy.
//
// This function used to be FOUR copy-pasted local helpers in the reportes with
// TWO different rule sets — `ComisionPorAfianzadora` and
// `ProduccionPorAfianzadora` knew only Aserta/Berkley/Tokio, while
// `LiquidacionComisiones` and `ComparativoAnual` also knew Sofimex/Chubb/
// Liberty. The consequence was live: the same Chubb fianza grouped as "Chubb"
// in one report and under its full legal name in another, and a carrier that
// changes its razón social (Chubb did) showed up as two rows in the stricter
// pair while the other pair showed one.

// Offline seed of the catalog, VERBATIM from scripts/seed_afianzadoras.py.
// It exists because the reportes group synchronously while the catalog loads
// async — without it the first paint would group by raw legal name and then
// flip. Keep in lockstep with that script; the live catalog wins and extends it.
export const CATALOGO_SEMILLA = [
  'Sofimex',
  'Dorama',
  'Aserta',
  'Insurgentes',
  'Chubb',
  'Berkley',
  'Liberty',
  'Tokio Marine',
  'Atlas',
  'Avanza',
  'Avla',
  'Mapfre',
  'CESCE',
  'Fiducia',
  'Crédito Afianzador',
  'Asecam',
  'Zurich',
];

export const SIN_EMISOR = 'Sin emisor';

/**
 * Uppercase, accent-stripped, for substring matching. The XML may or may not
 * carry accents ("CREDITO AFIANZADOR" vs "Crédito Afianzador"), so both sides
 * get normalized.
 *
 * @param {string} s
 * @return {string}
 */
function normalizar(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

/**
 * Catalog names paired with their match key, longest first so a short name can
 * never shadow a longer one that contains it.
 *
 * @param {Array<Object>} catalogo  - afianzadorasList from DataContext
 * @return {Array<[string, string]>} [matchKey, displayName]
 */
function tablaDeMatch(catalogo) {
  const porClave = new Map();
  // Seed first, live catalog second, so the catalog's spelling wins.
  CATALOGO_SEMILLA.forEach((n) => porClave.set(normalizar(n), n));
  (catalogo || []).forEach((a) => {
    const nombre = a && (a.nombre || a.name);
    if (nombre) porClave.set(normalizar(nombre), nombre);
  });
  return [...porClave.entries()].sort((a, b) => b[0].length - a[0].length);
}

/**
 * Canonical trade name for an afianzadora, from any legal-name variant.
 *
 * An unrecognized name comes back unchanged — better a long ugly label than two
 * carriers silently merged into one row.
 *
 * NOTE the match is a plain substring, so a short catalog name like
 * "Insurgentes" or "Atlas" could in principle match an unrelated company. That
 * is acceptable here because the input is always a fianza's EMISOR, which is
 * always an afianzadora. Do not reuse this on fiado or beneficiario names —
 * the sample XMLs contain "GASOLINERA INSURGENTES, S. A. DE C. V.", a fiado.
 *
 * @param {string|null|undefined} name  - the raw emisor from the XML
 * @param {Array<Object>} [catalogo]  - afianzadorasList, when available
 * @return {string}
 */
export function mapAfianzadoraName(name, catalogo = null) {
  if (!name) return SIN_EMISOR;
  const objetivo = normalizar(name);
  for (const [clave, display] of tablaDeMatch(catalogo)) {
    if (objetivo.includes(clave)) return display;
  }
  return String(name);
}

/**
 * Display names for a picker: the catalog merged with the seed, deduped and
 * alphabetical. Used by PendienteDialog's afianzadora field so a compromiso is
 * always filed against a named carrier — including when the catalog read fails.
 *
 * @param {Array<Object>} [catalogo]  - afianzadorasList from DataContext
 * @return {Array<{value: string, label: string}>}
 */
export function opcionesAfianzadora(catalogo = []) {
  const nombres = new Set(CATALOGO_SEMILLA);
  (catalogo || []).forEach((a) => {
    const nombre = a && (a.nombre || a.name);
    if (nombre) nombres.add(nombre);
  });
  return [...nombres]
    .sort((a, b) => a.localeCompare(b, 'es'))
    .map((n) => ({ value: n, label: n }));
}
